import _ from 'lodash';
import * as BrandDefinition from 'modules/BrandDefinition';
import {
  confirmLexicalStateInCTAComponent,
  confirmLexicalStateInTextComponent,
} from 'modules/draftjs2lexical';
import { createLexicalCTAEditor, createLexicalReferencesEditor, createLexicalTextEditor } from 'modules/Lexical/editors';
import * as lexicalHelpers from 'modules/Lexical/helpers';
import { select } from 'redux-saga/effects';
import {
  UIFontFaces as UIFontFacesSelector,
  flatFontsByRelationId as flatFontsByRelationIdSelector,
} from 'containers/Common/selectors';
import * as documentsSelectors from 'containers/Documents/selectors';
import * as ProjectSelectors from 'containers/Project/selectors';
import { relations as relationsSelector } from 'containers/Relations/selectors';
import * as Models from 'models';
import { isCallToAction, isReferenceCitationElementRelation, isTextComponent } from 'utils/entityType';
import { getHash, getNormalizedMainFontFamily, parseHash } from 'utils/fontHashes';
import { getReferencesAsString } from 'utils/getReferencesAsString';

export function* getUsedFontFaces(): Generator<unknown, Models.UIFontFace[]> {
  const projectType: ReturnTypeSaga<typeof ProjectSelectors.projectType> = yield select(ProjectSelectors.projectType);
  const relations: ReturnTypeSaga<typeof relationsSelector> = yield select(relationsSelector);
  const documents: ReturnTypeSaga<typeof documentsSelectors.documents> = yield select(documentsSelectors.documents);
  const UIFontFaces: ReturnTypeSaga<typeof UIFontFacesSelector> = yield select(UIFontFacesSelector);
  const fontsByRelationId: ReturnTypeSaga<typeof flatFontsByRelationIdSelector> = yield select(flatFontsByRelationIdSelector);
  const referenceCitationsByReferenceElements: ReturnTypeSaga<typeof documentsSelectors.referenceCitationsByReferenceElements> = yield select(
    documentsSelectors.referenceCitationsByReferenceElements,
  );
  let usedFontFaceHashes = new Set<string>();

  relations.forEach((relation, relationId): void => {
    if (!relation || !relationId) {
      return;
    }

    const documentId = (relation as Models.RegularRelationMap).get('documentId');
    const fonts = fontsByRelationId.get(relationId);

    if (isReferenceCitationElementRelation(relation)) {
      const styles = relation.get('styles');
      const fontFamily = BrandDefinition.getBrandFontValueFontFamily(
        fonts,
        { fontName: styles.get('fontFamily'), characterStyleName: styles.get('fontStyle') },
      );
      const mainFontFamily = getNormalizedMainFontFamily(fontFamily);
      const referencesOrder = referenceCitationsByReferenceElements.get(relationId);
      const html = getReferencesAsString(referencesOrder, projectType);

      const lexicalEditor = createLexicalReferencesEditor(html);
      const fontHashes = lexicalHelpers.getFontHashesForReferenceCitations(lexicalEditor, mainFontFamily);
      usedFontFaceHashes = new Set([...usedFontFaceHashes, ...fontHashes]);

    }

    if (!documentId) {
      return;
    }

    if (isTextComponent(relation)) {
      let document = documents.get(documentId) as Models.TextComponentMap;
      document = confirmLexicalStateInTextComponent(document, relation.get('styles').toJS(), fonts);
      const lexicalState = document.get('lexicalState');

      if (lexicalState) {
        const lexicalEditor = createLexicalTextEditor(lexicalState);
        const fontHashes = lexicalHelpers.getFontHashes(lexicalEditor) ;
        usedFontFaceHashes = new Set([...usedFontFaceHashes, ...fontHashes]);
      }

      return;
    }

    if (isCallToAction(relation)) {
      let document = documents.get(documentId) as Models.CallToActionMap;
      document = confirmLexicalStateInCTAComponent(document, relation.get('styles').toJS(), projectType, fonts);
      const lexicalState = document.get('lexicalState');
      if (lexicalState) {
        const lexicalEditor = createLexicalCTAEditor(lexicalState);
        const fontHashes = lexicalHelpers.getFontHashes(lexicalEditor);
        usedFontFaceHashes = new Set([...usedFontFaceHashes, ...fontHashes]);
      }
    }
  });

  const resultFontFaces: Models.UIFontFace[] = [];
  const fontFaces = UIFontFaces.toJS() as Models.UIFontFace[];
  const fontFacesByHash = _.groupBy(fontFaces, ({ fontFamily, fontWeight, fontStyle }) => getHash(fontFamily, fontWeight, fontStyle));

  usedFontFaceHashes.forEach((hash) => {
    const normalizedHash = hash.replaceAll('\'', '');
    const [fontFamily, fontWeight, fontStyle] = parseHash(normalizedHash);
    let resultFontFace = fontFacesByHash[getHash(fontFamily)];

    if (fontWeight && fontStyle) {
      const fontFace = fontFacesByHash[getHash(fontFamily, fontWeight, fontStyle)];
      resultFontFace = fontFace || resultFontFace;
    } else if (fontStyle) {
      const fontFace = fontFacesByHash[getHash(fontFamily, null, fontStyle)];
      resultFontFace = fontFace || resultFontFace;
    } else if (fontWeight) {
      const fontFace = fontFacesByHash[getHash(fontFamily, fontWeight)];
      resultFontFace = fontFace || resultFontFace;
    }
    if (!resultFontFace) {
      return;
    }
    resultFontFaces.push(...resultFontFace);
  });

  return resultFontFaces;
}
